import React, { useState, useCallback } from 'react';
import type {
  TableInteractionData,
  TableInteractionRow,
  NumberedOption,
} from '../utils/extractInteractiveTable';

// ─── Types ─────────────────────

interface YesNoAnswer {
  readonly choice: 'yes' | 'no' | null;
  readonly note: string;
}

interface SelectAnswer {
  readonly selected: string | null;
  readonly note: string;
}

type RowAnswer = YesNoAnswer | SelectAnswer | string;
type Answers = Record<number, RowAnswer>;

export interface TableInteractionFormProps {
  readonly data: TableInteractionData;
  readonly disabled?: boolean;
  readonly onSubmit: (message: string) => void;
}

// ─── 内部工具 ─────────────────────

/** 将用户填写的答案组装成发送给 AI 的文本 */
const buildSubmitMessage = (rows: readonly TableInteractionRow[], answers: Answers): string => {
  const lines = rows.map((row, i) => {
    const answer = answers[i];

    // text 类型
    if (typeof answer === 'string') {
      return `- ${row.itemName}：${answer.trim() || '（未填写）'}`;
    }

    // yesno 类型
    if ('choice' in answer) {
      const { choice, note } = answer as YesNoAnswer;
      const choiceText = choice === 'yes' ? '是' : choice === 'no' ? '否' : '（未选择）';
      return note.trim()
        ? `- ${row.itemName}：${choiceText}，${note.trim()}`
        : `- ${row.itemName}：${choiceText}`;
    }

    // select 类型
    const { selected, note } = answer as SelectAnswer;
    const selLabel = selected != null
      ? (row.options?.find(o => o.index === selected)?.label ?? selected)
      : '（未选择）';
    return note.trim()
      ? `- ${row.itemName}：选择 ${selLabel}，${note.trim()}`
      : `- ${row.itemName}：选择 ${selLabel}`;
  });

  return `已确认需求细节如下：\n${lines.join('\n')}`;
};

/** 初始化 Answers 状态 */
const initAnswers = (rows: readonly TableInteractionRow[]): Answers => {
  const init: Answers = {};
  rows.forEach((row, i) => {
    switch (row.inputType) {
      case 'yesno':  init[i] = { choice: null, note: '' } as YesNoAnswer; break;
      case 'select': init[i] = { selected: null, note: '' } as SelectAnswer; break;
      default:       init[i] = '';
    }
  });
  return init;
};

// ─── 子组件 ─────────────────────

/** 是/否 切换行 */
interface YesNoRowProps {
  readonly answer: YesNoAnswer;
  readonly disabled: boolean;
  readonly onToggle: (choice: 'yes' | 'no') => void;
  readonly onNoteChange: (note: string) => void;
}

const YesNoRow: React.FC<YesNoRowProps> = ({ answer, disabled, onToggle, onNoteChange }) => (
  <div className="tif__yesno">
    <button
      type="button"
      disabled={disabled}
      className={`tif__choice-btn${answer.choice === 'yes' ? ' tif__choice-btn--yes' : ''}`}
      onClick={() => onToggle('yes')}
    >
      是
    </button>
    <button
      type="button"
      disabled={disabled}
      className={`tif__choice-btn${answer.choice === 'no' ? ' tif__choice-btn--no' : ''}`}
      onClick={() => onToggle('no')}
    >
      否
    </button>
    <input
      type="text"
      disabled={disabled}
      className="tif__note-input"
      placeholder="补充说明（可选）"
      value={answer.note}
      onChange={e => onNoteChange(e.target.value)}
    />
  </div>
);

/** 编号选项单选行 */
interface SelectRowProps {
  readonly options: readonly NumberedOption[];
  readonly answer: SelectAnswer;
  readonly disabled: boolean;
  readonly onSelect: (index: string) => void;
  readonly onNoteChange: (note: string) => void;
}

const SelectRow: React.FC<SelectRowProps> = ({ options, answer, disabled, onSelect, onNoteChange }) => (
  <div className="tif__select">
    <div className="tif__select-options">
      {options.map(opt => (
        <button
          key={opt.index}
          type="button"
          disabled={disabled}
          className={`tif__option-btn${answer.selected === opt.index ? ' tif__option-btn--active' : ''}`}
          onClick={() => onSelect(opt.index)}
          title={opt.label}
        >
          <span className="tif__option-index">{opt.index}.</span>
          <span className="tif__option-label">{opt.label}</span>
        </button>
      ))}
    </div>
    <input
      type="text"
      disabled={disabled}
      className="tif__note-input"
      placeholder="补充说明（可选）"
      value={answer.note}
      onChange={e => onNoteChange(e.target.value)}
    />
  </div>
);

// ─── 主组件 ─────────────────────

/**
 * 表格交互表单
 *
 * 支持三种交互类型：
 * - select: 编号选项单选（最常见，AI 给出 1./2./3. 选项）
 * - yesno: 是/否 按钮
 * - text: 自由输入
 */
export const TableInteractionForm: React.FC<TableInteractionFormProps> = ({
  data,
  disabled = false,
  onSubmit,
}) => {
  const [answers, setAnswers] = useState<Answers>(() => initAnswers(data.rows));

  const handleYesNoToggle = useCallback((index: number, choice: 'yes' | 'no') => {
    setAnswers(prev => {
      const current = prev[index] as YesNoAnswer;
      return {
        ...prev,
        [index]: {
          choice: current.choice === choice ? null : choice,
          note: current.note,
        },
      };
    });
  }, []);

  const handleSelectOption = useCallback((index: number, optIndex: string) => {
    setAnswers(prev => {
      const current = prev[index] as SelectAnswer;
      return {
        ...prev,
        [index]: {
          selected: current.selected === optIndex ? null : optIndex,
          note: current.note,
        },
      };
    });
  }, []);

  const handleNoteChange = useCallback((index: number, note: string) => {
    setAnswers(prev => {
      const current = prev[index] as (YesNoAnswer | SelectAnswer);
      return { ...prev, [index]: { ...current, note } };
    });
  }, []);

  const handleTextChange = useCallback((index: number, value: string) => {
    setAnswers(prev => ({ ...prev, [index]: value }));
  }, []);

  const handleSubmit = useCallback(() => {
    const message = buildSubmitMessage(data.rows, answers);
    onSubmit(message);
  }, [data.rows, answers, onSubmit]);

  const renderRowInput = (row: TableInteractionRow, i: number) => {
    switch (row.inputType) {
      case 'yesno':
        return (
          <YesNoRow
            answer={answers[i] as YesNoAnswer}
            disabled={disabled}
            onToggle={choice => handleYesNoToggle(i, choice)}
            onNoteChange={note => handleNoteChange(i, note)}
          />
        );
      case 'select':
        return (
          <SelectRow
            options={row.options ?? []}
            answer={answers[i] as SelectAnswer}
            disabled={disabled}
            onSelect={optIndex => handleSelectOption(i, optIndex)}
            onNoteChange={note => handleNoteChange(i, note)}
          />
        );
      default:
        return (
          <input
            type="text"
            disabled={disabled}
            className="tif__text-input"
            placeholder="请输入..."
            value={(answers[i] as string) ?? ''}
            onChange={e => handleTextChange(i, e.target.value)}
          />
        );
    }
  };

  return (
    <div className="tif">
      <div className="tif__header">
        <i className="fas fa-table-list" />
        <span>请逐项确认需求细节</span>
      </div>

      <div className="tif__rows">
        {data.rows.map((row, i) => (
          <div key={i} className="tif__row">
            <div className="tif__item-name">{row.itemName}</div>
            <div className="tif__content">
              <div className="tif__hint">{row.contentHint}</div>
              {renderRowInput(row, i)}
            </div>
          </div>
        ))}
      </div>

      <div className="tif__footer">
        <button
          type="button"
          disabled={disabled}
          className="tif__submit-btn"
          onClick={handleSubmit}
        >
          <i className="fas fa-check" />
          提交确认
        </button>
      </div>
    </div>
  );
};
